// englishcontent7.js — Section 7: Exam Practice
// Improved full page:
// - Stronger GCSE English Language + Literature exam strategy
// - More exam-ready skills: timing, planning, evidence, comparison, evaluation, SPaG
// - Expanded worked examples and application tasks
// - Consistent topic structure for Grade 9 Ready lesson rendering

const makeTopic = (topic) => ({
  duration: '30-45 mins',
  xp: 120,
  difficulty: 'foundation',
  ...topic,
});

const tier = (label, text, critique) => ({ label, text, critique });

export const englishContent7 = {
  id: 7,
  title: 'Exam Practice',
  slug: 'exam-practice',
  color: 'orange',
  icon: 'FileText',
  description:
    'Mixed practice, strategy and modelled answers for GCSE English Language and English Literature.',
  topics: [

    // =========================================================
    // TOPIC 1 — MIXED PRACTICE TESTS
    // =========================================================
    makeTopic({
      id: 'eng-7-1',
      slug: 'mixed-practice-tests',
      title: 'Mixed Practice Tests',
      summary: 'Cross-skill practice mixing reading, writing, analysis and timed exam habits.',

      explainer: {
        title: 'Mixed Practice Tests',
        content: `
Mixed practice tests your FULL range of English skills in one session.

In GCSE English, you are rarely tested on one skill in isolation. A strong student needs to move quickly between:
- Reading accurately.
- Selecting evidence.
- Explaining language and structure.
- Evaluating viewpoints.
- Comparing texts.
- Writing clearly for a specific purpose, audience and form.

A mixed practice session might include:
- A retrieval question: finding explicit information from a text.
- An inference question: working out what is suggested but not directly stated.
- A language question: analysing words, imagery, sentence choices and techniques.
- A structure question: explaining how the text is organised.
- An evaluation question: judging how far you agree with a statement.
- A comparison question: comparing writers' ideas, methods or perspectives.
- A writing task: descriptive, narrative, persuasive, argumentative, explanatory or transactional writing.

The key exam habit is not just knowing what to do — it is knowing WHEN to stop.

A simple timing method:
1. Check the total marks.
2. Divide the time roughly by marks.
3. Spend more time on high-mark questions.
4. Leave a short proofreading buffer.
5. Move on when time is up, even if the answer is not perfect.

The biggest mark swings in English exams often come from:
- Spending too long on low-mark questions.
- Not planning high-mark answers.
- Forgetting to answer the exact wording of the question.
- Writing lots of explanation without evidence.
- Writing creatively but not controlling accuracy.
`
      },

      keyTerms: [
        { term: 'retrieval', definition: 'Finding information that is clearly stated in the text.' },
        { term: 'inference', definition: 'Working out an idea that is suggested but not directly stated.' },
        { term: 'evaluation', definition: 'Judging how successful or effective something is.' },
        { term: 'comparison', definition: 'Explaining similarities and differences between texts or ideas.' },
        { term: 'proofreading', definition: 'Checking spelling, punctuation, grammar and clarity before finishing.' }
      ],

      examStrategy: {
        title: 'Mixed Practice Exam Strategy',
        steps: [
          'Read the question first, then read the extract with that question in mind.',
          'Underline key words in the question, especially the focus: how, what, why, compare, evaluate.',
          'For reading answers, use short embedded quotations rather than long copied chunks.',
          'For writing answers, plan the opening, middle and ending before you start.',
          'Track time aggressively. A complete answer is usually better than one perfect paragraph and several blanks.'
        ]
      },

      interactiveLesson: {
        title: 'Exam Habits',
        steps: [
          {
            type: 'question',
            question: 'The biggest mark swings in exams usually come from:',
            options: ['Time management', 'Using a blue pen', 'Writing as much as possible without planning'],
            correct: 0
          },
          {
            type: 'question',
            question: 'Before writing a long answer, you should:',
            options: ['Plan the structure', 'Start immediately and hope ideas appear', 'Ignore the question wording'],
            correct: 0
          },
          {
            type: 'question',
            question: 'A good final exam habit is to leave:',
            options: ['2–5 minutes to proofread', 'Half the exam to proofread', 'No time because proofreading does not matter'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Example — Timing a Mixed Paper',
        content: `
Suppose an exam is 1 hour 45 minutes for 80 marks.

That gives 105 minutes for 80 marks.

Rough calculation:
105 ÷ 80 = about 1.3 minutes per mark.

Possible plan:
- Retrieval question, 4 marks → 5 minutes.
- Language analysis, 8 marks → 10 minutes.
- Structure analysis, 8 marks → 10 minutes.
- Evaluation, 20 marks → 25 minutes.
- Writing task, 40 marks → 45–50 minutes.
- Buffer and proofreading → 5 minutes.

Why this works:
The writing task is worth the most marks, so it deserves the most time. Spending 20 minutes on a 4-mark retrieval question is a poor exam decision, even if the answer becomes perfect.
`
      },

      workedExamples: [
        tier(
          'Basic',
          'I would spend about 1 minute per mark and make sure I finish every question.',
          'Clear but too general. It does not show how the student would divide time across the paper.'
        ),
        tier(
          'Secure',
          'For an 80-mark paper lasting 105 minutes, I would use roughly 1.3 minutes per mark, then reserve 5 minutes to proofread.',
          'Better because it uses the actual time and marks.'
        ),
        tier(
          'Strong',
          'I would protect the highest-mark writing section by limiting lower-mark questions strictly. A partly developed 4-mark answer hurts less than losing 10–15 marks because the writing task was rushed.',
          'Strong because it explains the judgement behind the timing strategy.'
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: 'A good rough timing guide in English exams is:',
          options: ['Use marks to guide minutes', 'Spend equal time on every question', 'Spend longest on the first question'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'You should read every question before you start writing.',
          correct: true
        },
        {
          type: 'mcq',
          question: 'Which task usually needs the most planning?',
          options: ['A high-mark writing task', 'A one-word retrieval answer', 'Writing your candidate number'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'It is usually sensible to leave a short proofreading buffer.',
          correct: true
        },
        {
          type: 'mcq',
          question: 'If you run out of time, the most likely cause is:',
          options: ['Spending too long on earlier low-mark questions', 'Using paragraphs', 'Reading the question carefully'],
          correct: 0
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `A paper is 1 hour for 40 marks. The writing task is worth 24 marks. Roughly how long should you spend on the writing task, and why?`,
        sentenceStarter: 'I would spend about...',
        hints: [
          'Work out minutes per mark.',
          'Give the writing task more time because it has more marks.',
          'Leave a few minutes to proofread.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Uses minutes-per-mark reasoning',
          'Explains why higher-mark questions need more time',
          'Accounts for proofreading time',
          'Writes in a clear full sentence'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why does spending LESS than planned time on an earlier question often hurt less than spending MORE than planned time on it?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 2 — ENGLISH LANGUAGE PAPER 1: FICTION
    // =========================================================
    makeTopic({
      id: 'eng-7-2',
      slug: 'english-language-practice-paper-1-fiction',
      title: 'English Language Practice Paper 1: Fiction',
      summary: 'Strategies for analysing fiction extracts and writing creatively under timed conditions.',

      explainer: {
        title: 'English Language Paper 1: Fiction',
        content: `
This paper usually focuses on an unseen fiction extract plus a creative writing task.

You are tested on two connected skills:
1. Reading fiction carefully.
2. Writing fiction or description effectively.

Common reading question types:
- Retrieval: find specific details from a section of the extract.
- Language analysis: explain how words, imagery and sentence choices create effects.
- Structure analysis: explain how the writer organises the text to interest the reader.
- Evaluation: judge how far you agree with a statement about the extract.

Common writing task types:
- Describe a place, person, object or scene.
- Write a story opening or narrative based on a prompt.
- Continue a moment of tension, mystery or conflict.

How to approach each reading question:
- Retrieval: copy exact details from the correct lines.
- Language: use short quotations, zoom in on words, explain effect.
- Structure: discuss focus, shifts, contrast, pace, openings, endings, viewpoint and chronology.
- Evaluation: give a clear judgement, support it with evidence, and explain why the writer is successful.

Creative writing strategy:
- Plan before writing.
- Start with a strong image, action or atmosphere.
- Use sensory detail: sight, sound, smell, touch, taste.
- Vary sentence length for pace.
- Use paragraphs to control shifts in time, place, mood or focus.
- Proofread for spelling, punctuation and grammar.

A strong Paper 1 student does not simply spot techniques. They explain how the technique shapes the reader's experience.
`
      },

      keyTerms: [
        { term: 'language', definition: 'The writer’s word, phrase, imagery and sentence choices.' },
        { term: 'structure', definition: 'How the text is organised across the whole extract.' },
        { term: 'focus shift', definition: 'A movement from one idea, place, character or mood to another.' },
        { term: 'cyclical structure', definition: 'A structure where the ending links back to the beginning.' },
        { term: 'atmosphere', definition: 'The mood or feeling created for the reader.' }
      ],

      examStrategy: {
        title: 'Paper 1 Exam Strategy',
        steps: [
          'For retrieval, stay inside the line range and copy precise details.',
          'For language, write about words and phrases, not vague general meaning.',
          'For structure, zoom out and track the whole extract from beginning to end.',
          'For evaluation, make a judgement and keep returning to the statement.',
          'For creative writing, plan the shape of the piece before crafting individual sentences.'
        ]
      },

      interactiveLesson: {
        title: 'Paper 1 Strategy',
        steps: [
          {
            type: 'question',
            question: 'For retrieval questions, you should:',
            options: ['Copy exact phrases from the correct lines', 'Paraphrase everything from memory', 'Use quotations from outside the extract'],
            correct: 0
          },
          {
            type: 'question',
            question: 'For STRUCTURE questions, you should comment on:',
            options: ['How the text is organised', 'Individual words only', 'Only punctuation'],
            correct: 0
          },
          {
            type: 'question',
            question: 'For creative writing, strong openings often:',
            options: ['Drop the reader into a clear image, action or mood', 'Start with a long explanation of everything', 'Avoid description'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Example — Language Analysis',
        content: `
Question: How does the writer use language to describe the storm?

Text:
"The wind snarled through the rigging like a beast denied its prey."

Model answer:
The writer presents the storm as aggressive and threatening. The personification "snarled" makes the wind sound animal-like, as if it has a violent intention. The simile "like a beast denied its prey" intensifies this threat because it suggests the storm is frustrated and hungry for destruction. This creates tension for the reader because the weather feels alive and dangerous, not simply unpleasant.

Why this works:
- It answers the question directly.
- It uses a short quotation.
- It names techniques.
- It zooms in on individual words.
- It explains the effect on the reader.
`
      },

      workedExamples: [
        tier(
          'Basic',
          'The writer uses personification to describe the wind. This makes it scary.',
          'This is relevant but underdeveloped. It names a technique but does not explore the quotation closely.'
        ),
        tier(
          'Secure',
          'The verb "snarled" personifies the wind, making it seem aggressive and animal-like.',
          'This is clearer because it zooms in on one word and explains its connotations.'
        ),
        tier(
          'Strong',
          'The verb "snarled" gives the storm a predatory quality, while "denied its prey" suggests frustrated violence. The storm feels less like weather and more like an antagonist.',
          'Strong because it interprets the combined effect of the language choices.'
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: 'The verb "snarled" is an example of:',
          options: ['Personification', 'A statistic', 'A heading'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Retrieval questions usually reward accurate details from the correct lines.',
          correct: true
        },
        {
          type: 'mcq',
          question: 'Which is a structural feature?',
          options: ['A shift from calm to panic', 'The adjective "red"', 'A simile about a bird'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Creative writing should still be carefully structured.',
          correct: true
        },
        {
          type: 'mcq',
          question: 'A good language paragraph should usually include:',
          options: ['A short quotation and explanation of effect', 'A long copied paragraph with no comment', 'Only a technique name'],
          correct: 0
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Read: "The silence thickened like fog, pressing against the walls."

Write a short PEE paragraph analysing the writer's language.`,
        sentenceStarter: 'The writer presents the silence as...',
        hints: [
          'Name the simile.',
          'Zoom in on "thickened".',
          'Explain how the silence feels physical or oppressive.',
          'Link the effect to the reader.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Makes a clear point',
          'Uses a short quotation',
          'Names or describes a method',
          'Zooms in on one word or phrase',
          'Explains the effect on the reader'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why is STRUCTURE often harder to analyse than language — and what makes a strong structural point?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 3 — ENGLISH LANGUAGE PAPER 2: NON-FICTION
    // =========================================================
    makeTopic({
      id: 'eng-7-3',
      slug: 'english-language-practice-paper-2-non-fiction',
      title: 'English Language Practice Paper 2: Non-Fiction',
      summary: 'Compare viewpoints and write non-fiction under timed conditions.',

      explainer: {
        title: 'English Language Paper 2: Non-Fiction',
        content: `
This paper usually has TWO non-fiction extracts plus a writing task.

The extracts may include:
- A newspaper article.
- A letter.
- A diary entry.
- A speech.
- A travel account.
- An essay or opinion piece.
- A 19th-century text and a modern text.

Common question types:
- Retrieval from one source.
- Summary of similarities or differences across both sources.
- Language analysis on one source.
- Comparison of writers' viewpoints and methods.
- Non-fiction writing presenting a viewpoint.

Comparison strategy:
- Compare point by point.
- Use both texts in each paragraph.
- Use signpost language: both, similarly, whereas, however, in contrast.
- Compare ideas AND methods.
- Explain the writers' attitudes, not just the topic.

Useful comparison sentence frames:
- "Both writers present..."
- "However, Source A suggests..., whereas Source B..."
- "This difference may reflect..."
- "The modern writer appears..., while the 19th-century writer..."

For the writing task:
- Identify the form: letter, article, speech, essay or leaflet.
- Identify the audience: students, parents, public, council, head teacher.
- Identify the purpose: argue, persuade, explain, inform, advise.
- Choose a matching register: formal, lively, serious, reflective or urgent.
- Use rhetorical devices deliberately, not randomly.

A strong Paper 2 answer shows control: controlled comparison in reading, controlled voice in writing.
`
      },

      keyTerms: [
        { term: 'viewpoint', definition: 'The writer’s opinion, attitude or perspective.' },
        { term: 'perspective', definition: 'The position from which a writer sees or presents an issue.' },
        { term: 'register', definition: 'The level of formality or style used for a particular audience.' },
        { term: 'transactional writing', definition: 'Writing designed for a real-world purpose, such as a letter, speech or article.' },
        { term: 'signpost language', definition: 'Words and phrases that show comparison, contrast or development.' }
      ],

      examStrategy: {
        title: 'Paper 2 Exam Strategy',
        steps: [
          'Read both sources with the question focus in mind.',
          'For summary, compare ideas rather than analysing language in detail.',
          'For language, zoom in on one source only if the question asks for one source.',
          'For comparison, write about both writers in every paragraph.',
          'For viewpoint writing, match form, audience and purpose from the first sentence.'
        ]
      },

      interactiveLesson: {
        title: 'Paper 2 Strategy',
        steps: [
          {
            type: 'question',
            question: 'The comparison question is usually strongest when you compare:',
            options: ['Point by point', 'Text A completely, then Text B completely', 'Only the first text'],
            correct: 0
          },
          {
            type: 'question',
            question: 'Each comparison paragraph should usually include:',
            options: ['Evidence from both texts', 'Evidence from only one text', 'No evidence'],
            correct: 0
          },
          {
            type: 'question',
            question: 'A formal letter should use:',
            options: ['Standard English', 'Slang and emojis', 'Text-message abbreviations'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Example — Comparison Paragraph',
        content: `
Question: Compare how the writers present the city.

Model paragraph:
Both writers present the city as overwhelming. In Source A, the writer describes the streets as "choked with noise", suggesting the city is suffocating and unpleasant. Similarly, in Source B, the Victorian writer calls London "a great, humming hive", which also presents the city as crowded and full of movement. However, Source A uses the negative verb "choked", whereas Source B uses a more admiring metaphor of a "hive". This suggests the modern writer feels exhausted by the city, while the Victorian writer is impressed by its energy and scale.

Why this works:
- It compares both texts in one paragraph.
- It uses short quotations from both.
- It compares the writers' attitudes.
- It explains the effect of language choices.
`
      },

      workedExamples: [
        tier(
          'Basic',
          'Both writers describe the city as busy. Source A says it is noisy and Source B says it is like a hive.',
          'This identifies a similarity but does not analyse methods or attitudes in enough detail.'
        ),
        tier(
          'Secure',
          'Both writers present the city as busy, but Source A is more negative because "choked" suggests discomfort, while Source B seems more impressed by the "humming hive".',
          'This is better because it identifies a contrast in attitude.'
        ),
        tier(
          'Strong',
          'The writers both use images of density, but they shape the reader’s response differently: "choked" makes the city feel oppressive, while "humming hive" suggests purposeful collective energy.',
          'Strong because it compares methods and effects precisely.'
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: 'Which signpost shows contrast?',
          options: ['Whereas', 'Similarly', 'Also'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'In comparison questions, you should usually quote from both texts.',
          correct: true
        },
        {
          type: 'mcq',
          question: 'A speech opening should usually:',
          options: ['Address the audience directly', 'Avoid any audience awareness', 'Use no paragraphs'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Viewpoint means the writer’s attitude or opinion.',
          correct: true
        },
        {
          type: 'mcq',
          question: 'Which phrase is useful for comparison?',
          options: ['In contrast', 'The end', 'Once upon a time'],
          correct: 0
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Both Source A and Source B describe crowds.

Source A: "a forest of hats"
Source B: "a river of people, ceaseless and hurrying"

Write one comparison paragraph on how each writer describes the crowd.`,
        sentenceStarter: 'Both writers use natural imagery to describe the crowd...',
        hints: [
          'Quote both sources.',
          'Compare "forest" and "river".',
          'Explain whether the crowd feels still, moving, threatening or impressive.',
          'Use a comparison signpost.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Quotes from both texts',
          'Uses comparison signposting',
          'Explains the effect of each image',
          'Compares writer attitudes',
          'Keeps the paragraph focused on the question'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why does comparing a 19th-century non-fiction text with a modern text often reveal more than comparing two modern texts?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 4 — UNSEEN POETRY
    // =========================================================
    makeTopic({
      id: 'eng-7-4',
      slug: 'english-literature-unseen-poetry',
      title: 'English Literature: Unseen Poetry',
      summary: 'Analyse a poem you have never seen before with confidence and structure.',

      explainer: {
        title: 'Unseen Poetry',
        content: `
Unseen poetry means you analyse a poem that you have not studied before.

This can feel difficult because you cannot rely on memorised notes. Instead, you must rely on your reading skills.

First reading:
- What is literally happening?
- Who is speaking?
- What is the mood?
- What is the main idea or theme?

Second reading:
- Which words stand out?
- What images or symbols are repeated?
- How is the poem structured?
- Does the mood change?
- What does the ending reveal?

Things to analyse:
- Language: imagery, metaphor, simile, personification, word choice.
- Structure: stanza lengths, line breaks, enjambment, caesura, repetition, shifts.
- Form: whether it looks controlled, fragmented, regular, irregular, dramatic or conversational.
- Voice: who seems to be speaking and how they sound.
- Theme: what the poem is really exploring.

A strong unseen answer does not need to identify every technique. It needs to make thoughtful points that are clearly linked to the question.

For unseen comparison:
- Start with the main similarity or difference.
- Compare one idea at a time.
- Use short quotations from both poems.
- Explain how each poet creates mood or meaning differently.
`
      },

      keyTerms: [
        { term: 'enjambment', definition: 'When a sentence or phrase runs over from one line to the next.' },
        { term: 'caesura', definition: 'A pause within a line, often created by punctuation.' },
        { term: 'stanza', definition: 'A group of lines in a poem.' },
        { term: 'speaker', definition: 'The voice speaking in the poem.' },
        { term: 'refrain', definition: 'A repeated line or phrase in a poem.' }
      ],

      examStrategy: {
        title: 'Unseen Poetry Strategy',
        steps: [
          'Read the poem twice before writing.',
          'Write a one-sentence overview of the poem’s meaning or mood.',
          'Choose 2–4 short quotations rather than trying to cover everything.',
          'Analyse both language and structure.',
          'Keep linking every point back to the question.'
        ]
      },

      interactiveLesson: {
        title: 'Unseen Poetry',
        steps: [
          {
            type: 'question',
            question: 'How many times should you read an unseen poem before writing?',
            options: ['At least twice', 'Never read it fully', 'Only read the title'],
            correct: 0
          },
          {
            type: 'question',
            question: 'A strong unseen answer covers:',
            options: ['Language, structure, theme and mood', 'Only rhyme', 'Only the poet’s biography'],
            correct: 0
          },
          {
            type: 'question',
            question: 'Every point should link to:',
            options: ['The question', 'Your favourite poem', 'A random technique list'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Example — Unseen Poetry Analysis',
        content: `
Poem extract:
"And still, the dawn /
still, the small birds /
still, the hope I never asked to keep."

Model mini-analysis:
The poet creates a sense of quiet endurance. The repeated word "still" acts almost like a refrain, suggesting that hope continues despite difficulty. The image of "dawn" implies renewal, while "the hope I never asked to keep" is more conflicted, suggesting that hope can feel unwanted or painful. The line breaks slow the rhythm, making each small detail feel fragile but important.

Why this works:
- It identifies a mood.
- It comments on repetition.
- It explores individual images.
- It links structure to meaning.
`
      },

      workedExamples: [
        tier(
          'Basic',
          'The poem is about hope. The poet repeats "still" to show hope.',
          'This understands the basic idea but does not analyse the effect in enough depth.'
        ),
        tier(
          'Secure',
          'The repetition of "still" suggests endurance because hope continues even after difficulty.',
          'This is clearer because it links repetition to meaning.'
        ),
        tier(
          'Strong',
          'The repeated "still" creates a quiet, almost reluctant rhythm of survival, as though hope persists even when the speaker has not chosen it.',
          'Strong because it explores a more subtle interpretation of the speaker’s feelings.'
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: 'The repeated word "still" creates:',
          options: ['A refrain-like rhythm', 'A statistic', 'A stage direction'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Enjambment can create flow between lines.',
          correct: true
        },
        {
          type: 'mcq',
          question: 'A caesura is:',
          options: ['A pause within a line', 'A type of newspaper', 'The final paragraph of an essay'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Unseen poetry answers should still use evidence.',
          correct: true
        },
        {
          type: 'mcq',
          question: 'Which is a structural feature?',
          options: ['Irregular stanza lengths', 'The adjective "cold"', 'The noun "tree"'],
          correct: 0
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Poem:
"The house waited.
The windows watched.
The door — unlocked, patient — stayed shut."

Write a short paragraph analysing the mood this poem creates.`,
        sentenceStarter: 'The poet creates a mood of...',
        hints: [
          'Comment on personification.',
          'Comment on the dash pause.',
          'Think about whether the house feels safe, eerie, lonely or expectant.',
          'Link your point to mood.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Identifies a clear mood',
          'Uses a short quotation',
          'Comments on personification or punctuation',
          'Explains the effect on the reader',
          'Links back to the question'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why do examiners reward candidates who comment on form and structure, not just individual words?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 5 — SHAKESPEARE
    // =========================================================
    makeTopic({
      id: 'eng-7-5',
      slug: 'english-literature-shakespeare-exam-practice',
      title: 'English Literature: Shakespeare Exam Practice',
      summary: 'Answer extract-to-whole Shakespeare questions with method, context and confidence.',

      explainer: {
        title: 'Shakespeare Exam Practice',
        content: `
Shakespeare questions often give you an extract and ask you to write about a character, relationship, theme or idea.

Your job is to:
- Analyse the extract closely.
- Link the extract to the wider play.
- Explore Shakespeare's methods.
- Refer to relevant context.
- Keep answering the exact question.

Common focus areas:
- Power and ambition.
- Love and conflict.
- Fate and free will.
- Appearance and reality.
- Gender expectations.
- Kingship and authority.
- The supernatural.
- Violence, revenge or justice.

A strong Shakespeare answer includes:
- A clear overview sentence.
- Short quotations from the extract.
- References to elsewhere in the play.
- Analysis of language, form and dramatic methods.
- Relevant context that supports the point.
- Writer's purpose: why Shakespeare presents the idea in this way.

Remember: Shakespeare wrote plays for performance. You can comment on:
- Stage directions.
- Entrances and exits.
- Soliloquies.
- Asides.
- Dramatic irony.
- Dialogue and interruptions.
- How an audience might respond.
`
      },

      keyTerms: [
        { term: 'soliloquy', definition: 'A speech where a character speaks their thoughts aloud, often alone on stage.' },
        { term: 'aside', definition: 'A brief comment spoken to the audience or another character but not heard by others on stage.' },
        { term: 'dramatic irony', definition: 'When the audience knows something that one or more characters do not.' },
        { term: 'tragic hero', definition: 'A central character in a tragedy whose flaw contributes to their downfall.' },
        { term: 'context', definition: 'Relevant social, historical or cultural knowledge that helps explain meaning.' }
      ],

      examStrategy: {
        title: 'Shakespeare Answer Strategy',
        steps: [
          'Read the extract and question carefully.',
          'Underline the key focus, such as ambition, conflict, power or guilt.',
          'Plan 3 points: at least one close extract point and one wider play point.',
          'Analyse Shakespeare’s methods, including dramatic methods where relevant.',
          'Use context to deepen analysis, not as a bolted-on history paragraph.'
        ]
      },

      interactiveLesson: {
        title: 'Shakespeare Strategy',
        steps: [
          {
            type: 'question',
            question: 'A Shakespeare extract answer should link to:',
            options: ['The wider play', 'Only the extract', 'Only modern life'],
            correct: 0
          },
          {
            type: 'question',
            question: 'A soliloquy is:',
            options: ['A speech revealing a character’s thoughts', 'A newspaper article', 'A type of rhyme'],
            correct: 0
          },
          {
            type: 'question',
            question: 'Context should be used to:',
            options: ['Support analysis', 'Replace analysis', 'Avoid quotations'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Example — Shakespeare Paragraph',
        content: `
Question: How does Shakespeare present Macbeth's ambition?

Model paragraph:
Shakespeare presents Macbeth's ambition as powerful but morally dangerous. When Macbeth calls the crown a "fruitless crown", the adjective "fruitless" suggests that power alone is not enough for him because he also wants a secure legacy. This reveals how ambition grows beyond the original act of becoming king. In the wider play, Macbeth's ambition causes him to murder Duncan and later become increasingly tyrannical. For a Jacobean audience, this would have been especially disturbing because killing a king challenged the Divine Right of Kings and the natural order.

Why this works:
- It answers the question.
- It analyses a key word.
- It links to the wider play.
- It uses context relevantly.
`
      },

      workedExamples: [
        tier(
          'Basic',
          'Macbeth is ambitious because he wants to be king.',
          'This is accurate but too simple. It does not analyse Shakespeare’s methods.'
        ),
        tier(
          'Secure',
          'The phrase "fruitless crown" shows Macbeth’s ambition because he is not satisfied with becoming king unless his power continues through his descendants.',
          'This is stronger because it explains the quotation.'
        ),
        tier(
          'Strong',
          'By describing the crown as "fruitless", Macbeth reduces kingship to inheritance and control, showing how ambition has distorted his values. Shakespeare presents ambition as self-consuming rather than heroic.',
          'Strong because it interprets the deeper implications of the language.'
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: 'Which is a dramatic method?',
          options: ['Soliloquy', 'Percentage', 'Subheading'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'A Shakespeare answer should usually link the extract to the wider play.',
          correct: true
        },
        {
          type: 'mcq',
          question: 'Context should be:',
          options: ['Relevant to the argument', 'Random historical facts', 'Longer than the analysis'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Shakespeare wrote plays to be performed.',
          correct: true
        },
        {
          type: 'mcq',
          question: 'A strong overview sentence should:',
          options: ['Give a clear argument about the question', 'Retell the whole play', 'List every character'],
          correct: 0
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Imagine a Shakespeare extract where a king angrily orders a subject to leave.

Write a PEE + context paragraph on how Shakespeare presents authority.`,
        sentenceStarter: 'Shakespeare presents authority as...',
        hints: [
          'Invent a short quotation, such as "Begone!"',
          'Comment on imperatives or stage action.',
          'Mention hierarchy or kingship if relevant.',
          'Link to audience response.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Clear point about authority',
          'Short quotation or invented extract detail',
          'Analysis of language or dramatic method',
          'Relevant context',
          'Link to Shakespeare’s purpose'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why is it not enough to write about context separately from the quotation in a Shakespeare essay?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 6 — 19TH-CENTURY PROSE
    // =========================================================
    makeTopic({
      id: 'eng-7-6',
      slug: 'english-literature-19th-century-prose-exam-practice',
      title: 'English Literature: 19th-Century Prose Exam Practice',
      summary: 'Write strong extract-to-whole answers on Victorian or 19th-century novels.',

      explainer: {
        title: '19th-Century Prose Exam Practice',
        content: `
The 19th-century novel question usually gives you an extract and asks you to write about a character, theme or relationship.

Common GCSE texts include:
- A Christmas Carol.
- The Strange Case of Dr Jekyll and Mr Hyde.
- Frankenstein.
- Jane Eyre.
- Great Expectations.
- Pride and Prejudice.
- The Sign of Four.

The question normally expects you to:
- Analyse the extract closely.
- Refer to the wider novel.
- Explore the writer's methods.
- Use relevant context.
- Explain the writer's message or purpose.

Useful areas of context:
- Victorian poverty.
- Class divisions.
- Religion and morality.
- Gender roles.
- Industrialisation.
- Science and anxiety.
- Crime, punishment and reputation.
- Family, inheritance and social status.

A strong 19th-century prose answer avoids simply retelling the plot. Instead, it asks:
- What is the writer criticising?
- What is the writer encouraging the reader to feel?
- How does the character develop across the novel?
- How does the extract connect to the whole text?
- How does context shape the writer's message?

Good structure:
1. Overview sentence answering the question.
2. Extract analysis with quotation.
3. Link to wider novel.
4. Context linked to interpretation.
5. Writer's purpose.
`
      },

      keyTerms: [
        { term: 'novella', definition: 'A short novel, often with a focused plot and limited number of characters.' },
        { term: 'social criticism', definition: 'Writing that exposes problems in society.' },
        { term: 'motif', definition: 'A repeated image, idea or symbol.' },
        { term: 'character arc', definition: 'How a character changes or develops across a text.' },
        { term: 'moral message', definition: 'The lesson or value the writer encourages the reader to consider.' }
      ],

      examStrategy: {
        title: '19th-Century Novel Strategy',
        steps: [
          'Identify the question focus: character, theme, relationship or setting.',
          'Use the extract as your starting point, not your whole answer.',
          'Select short quotations and zoom in on key words.',
          'Connect to at least two other moments in the novel.',
          'Use context to explain why the writer’s message mattered.'
        ]
      },

      interactiveLesson: {
        title: '19th-Century Prose Strategy',
        steps: [
          {
            type: 'question',
            question: 'An extract-to-whole answer should discuss:',
            options: ['The extract and the wider novel', 'Only the first sentence', 'Only the author’s biography'],
            correct: 0
          },
          {
            type: 'question',
            question: 'Context should help explain:',
            options: ['The writer’s message and reader response', 'The page number', 'The font size'],
            correct: 0
          },
          {
            type: 'question',
            question: 'A character arc means:',
            options: ['How a character changes across the text', 'A curved line in a diagram', 'A list of quotations'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Example — 19th-Century Novel Paragraph',
        content: `
Question: How does Dickens present Scrooge as an outsider?

Model paragraph:
Dickens presents Scrooge as an outsider through his emotional isolation and rejection of community. In the extract, Scrooge "kept it up" only in silence, suggesting that he deliberately maintains his coldness. The verb "kept" implies effort, as though isolation is something he protects. In the wider novel, this separation is challenged by the Ghosts, who force him to witness the human consequences of selfishness. In a Victorian context, where poverty and social responsibility were urgent concerns, Dickens uses Scrooge to criticise those who ignore the suffering around them.

Why this works:
- It answers the question.
- It analyses a quotation.
- It links to the whole novel.
- It connects context to Dickens' message.
`
      },

      workedExamples: [
        tier(
          'Basic',
          'Scrooge is lonely and does not like people.',
          'This is true but descriptive. It needs evidence and analysis.'
        ),
        tier(
          'Secure',
          'Dickens presents Scrooge as isolated because he "kept it up" in silence, which suggests he deliberately rejects other people.',
          'This is better because it uses evidence and explains a word choice.'
        ),
        tier(
          'Strong',
          'The phrase "kept it up" implies that Scrooge’s isolation is not accidental but actively maintained, making him a symbol of selfish individualism that Dickens wants the reader to reject.',
          'Strong because it links language to writer’s purpose.'
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: 'Context for A Christmas Carol may include:',
          options: ['Victorian poverty and charity', 'Smartphones', 'Space travel'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Linking to the wider novel is rewarded in extract-to-whole answers.',
          correct: true
        },
        {
          type: 'mcq',
          question: 'Which is the strongest use of context?',
          options: ['Dickens criticises Victorian selfishness through Scrooge', 'Victorian people wore clothes', 'The book is old'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'A 19th-century prose answer should only retell the plot.',
          correct: false
        },
        {
          type: 'mcq',
          question: 'A motif is:',
          options: ['A repeated image, idea or symbol', 'A spelling mistake', 'A type of exam paper'],
          correct: 0
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Imagine an extract from a 19th-century novel where a wealthy character ignores a poor child.

Write a paragraph explaining how the writer presents social inequality.`,
        sentenceStarter: 'The writer presents social inequality as...',
        hints: [
          'Create a short quotation such as "turned away".',
          'Zoom in on the action or verb.',
          'Mention class or poverty.',
          'Explain the writer’s possible message.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Makes a clear point about inequality',
          'Uses a short quotation or extract detail',
          'Analyses a word or action',
          'Links to wider social context',
          'Explains writer’s purpose'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why do many 19th-century novels use individual characters to criticise wider social problems?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 7 — MODERN TEXTS AND ANTHOLOGY POETRY
    // =========================================================
    makeTopic({
      id: 'eng-7-7',
      slug: 'english-literature-modern-texts-and-anthology-poetry',
      title: 'English Literature: Modern Texts and Anthology Poetry',
      summary: 'Revise modern drama/prose and compare studied poems effectively.',

      explainer: {
        title: 'Modern Texts and Anthology Poetry',
        content: `
Modern texts and anthology poetry require strong knowledge of studied material.

Modern texts may include:
- A modern play.
- A modern novel.
- A post-1914 prose or drama text.

Poetry anthology questions usually ask you to compare one named poem with another poem you have studied.

For modern texts:
- Know the plot and key moments.
- Know major characters and relationships.
- Know key themes.
- Learn short, flexible quotations.
- Understand the writer's message and context.
- Analyse language, structure and dramatic methods.

For anthology poetry:
- Know each poem's main theme.
- Learn 3–5 key quotations per poem.
- Know the speaker and mood.
- Know key structural features.
- Practise comparing poems by theme, not by memorised order.

Common poetry comparison themes:
- Power.
- Conflict.
- Nature.
- Memory.
- Identity.
- Loss.
- Love.
- Anger.
- Fear.
- Control.

Strong comparison structure:
1. Start with a clear comparative overview.
2. Compare one shared theme.
3. Use evidence from both poems.
4. Analyse methods from both poets.
5. Explain a difference in perspective, tone or message.

Avoid:
- Writing everything you know about Poem A, then everything about Poem B.
- Forgetting the named poem.
- Using quotations without analysis.
- Comparing only content without methods.
`
      },

      keyTerms: [
        { term: 'anthology', definition: 'A collection of poems studied as part of the course.' },
        { term: 'comparative overview', definition: 'An opening sentence that explains the main similarity or difference.' },
        { term: 'dramatic method', definition: 'A technique used in plays, such as stage directions, entrances, exits or dialogue.' },
        { term: 'tone', definition: 'The attitude or emotional quality of the writing.' },
        { term: 'perspective', definition: 'The viewpoint or position from which an idea is presented.' }
      ],

      examStrategy: {
        title: 'Modern Text and Poetry Strategy',
        steps: [
          'For modern texts, revise by theme and character, not only by plot order.',
          'For poetry, prepare comparison pairs for major themes.',
          'Use short quotations that can be applied to several questions.',
          'Compare methods as well as ideas.',
          'Keep returning to the writer’s purpose or message.'
        ]
      },

      interactiveLesson: {
        title: 'Modern Texts and Poetry',
        steps: [
          {
            type: 'question',
            question: 'Anthology poetry comparison should usually be organised:',
            options: ['Point by point', 'Poem A completely then Poem B completely', 'By quotation length'],
            correct: 0
          },
          {
            type: 'question',
            question: 'A modern drama answer can analyse:',
            options: ['Stage directions and dialogue', 'Only chapter titles', 'Only the front cover'],
            correct: 0
          },
          {
            type: 'question',
            question: 'A strong poetry comparison should compare:',
            options: ['Ideas and methods', 'Only the number of lines', 'Only whether you like the poem'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Example — Poetry Comparison',
        content: `
Question: Compare how poets present the power of nature.

Model paragraph:
Both poets present nature as more powerful than humans, but they do this in different ways. In one poem, nature is shown as overwhelming through the image of a mountain that "towered" above the speaker, making humanity seem small and vulnerable. In the second poem, nature's power is slower and more destructive, as the sea is described as "eating" away at the land. While the first poet presents nature as immediately intimidating, the second presents it as patient and unstoppable. Both methods remind the reader that human control is limited.

Why this works:
- It compares both poems in one paragraph.
- It compares the same theme.
- It comments on methods.
- It explains a difference in perspective.
`
      },

      workedExamples: [
        tier(
          'Basic',
          'Both poems are about nature being powerful.',
          'This gives a comparison but lacks evidence and method.'
        ),
        tier(
          'Secure',
          'Both poets show nature as stronger than humans, but one presents it as sudden and frightening, while the other presents it as slow and unstoppable.',
          'This is stronger because it identifies a difference.'
        ),
        tier(
          'Strong',
          'The poets both undermine human confidence: one uses scale to make the speaker seem insignificant, while the other uses erosion imagery to suggest nature wins through time rather than force.',
          'Strong because it compares methods and interpretations.'
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: 'A poetry comparison paragraph should usually include:',
          options: ['Both poems', 'Only one poem', 'No quotations'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Modern text answers should link methods to writer’s purpose.',
          correct: true
        },
        {
          type: 'mcq',
          question: 'Which is a useful comparison word?',
          options: ['Whereas', 'Suddenly', 'Because of the weather'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'You should compare only what happens, not how writers present ideas.',
          correct: false
        },
        {
          type: 'mcq',
          question: 'Tone means:',
          options: ['The attitude or emotional quality of writing', 'The exam room temperature', 'The number of pages'],
          correct: 0
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Two poems present memory.

Poem A presents memory as painful and unavoidable.
Poem B presents memory as comforting but fragile.

Write a comparison paragraph explaining the difference.`,
        sentenceStarter: 'Both poets present memory as powerful, but...',
        hints: [
          'Compare the same theme.',
          'Use contrast language.',
          'Invent short quotations if needed.',
          'Explain tone and message.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Compares both poems',
          'Uses clear contrast language',
          'Explains tone or perspective',
          'Analyses methods, not just ideas',
          'Links to the theme of memory'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `Why is a point-by-point comparison usually stronger than writing one paragraph on each poem separately?`,
        xpReward: 50
      }
    }),

    // =========================================================
    // TOPIC 8 — FINAL EXAM TECHNIQUE AND MODEL ANSWERS
    // =========================================================
    makeTopic({
      id: 'eng-7-8',
      slug: 'final-exam-technique-and-model-answers',
      title: 'Final Exam Technique and Model Answers',
      summary: 'Use planning, paragraph structure and proofreading to turn knowledge into marks.',

      explainer: {
        title: 'Final Exam Technique and Model Answers',
        content: `
Final exam technique is about converting knowledge into marks.

Many students know more than their answer shows. This happens when they:
- Do not answer the exact question.
- Use evidence but do not explain it.
- Analyse techniques without linking to meaning.
- Forget writer's purpose.
- Lose control of timing.
- Write creatively but with weak accuracy.
- Leave no time to proofread.

A strong answer usually has:
- A direct opening judgement.
- Clear paragraphs.
- Short evidence.
- Close analysis.
- Links to the question.
- Relevant context where appropriate.
- A controlled ending.

Useful paragraph structures:
PEE:
- Point.
- Evidence.
- Explain.

PETAL:
- Point.
- Evidence.
- Technique.
- Analysis.
- Link.

WHAT-HOW-WHY:
- What is the writer showing?
- How do they show it?
- Why might they show it this way?

For writing tasks, use:
- TAP: Tone, Audience, Purpose.
- FORM: Features, Organisation, Register, Message.
- SPaG check: spelling, punctuation and grammar.

Proofreading checklist:
- Have I answered the exact question?
- Have I used paragraphs?
- Have I used accurate punctuation?
- Have I checked tense consistency?
- Have I corrected obvious spelling errors?
- Have I varied sentence openings?
- Have I used quotations accurately?

Grade 9 style writing is not about using the longest words. It is about precision, control, clarity and thoughtful interpretation.
`
      },

      keyTerms: [
        { term: 'judgement', definition: 'A clear decision or argument about the text or question.' },
        { term: 'embedded quotation', definition: 'A short quotation smoothly included inside your own sentence.' },
        { term: 'writer’s purpose', definition: 'The reason the writer presents an idea, character or theme in a particular way.' },
        { term: 'coherence', definition: 'How clearly ideas connect across an answer.' },
        { term: 'accuracy', definition: 'Correct spelling, punctuation, grammar and expression.' }
      ],

      examStrategy: {
        title: 'Final Exam Strategy',
        steps: [
          'Turn the question into a one-sentence argument before writing.',
          'Use short quotations and analyse them closely.',
          'For Literature, link extract, whole text, context and writer’s purpose.',
          'For Language, focus tightly on method and effect.',
          'Proofread for meaning first, then technical accuracy.'
        ]
      },

      interactiveLesson: {
        title: 'Final Technique',
        steps: [
          {
            type: 'question',
            question: 'A strong opening sentence should usually:',
            options: ['Answer the question directly', 'Retell the whole plot', 'Apologise for the answer'],
            correct: 0
          },
          {
            type: 'question',
            question: 'PETAL stands for:',
            options: ['Point, Evidence, Technique, Analysis, Link', 'Plot, Ending, Title, Author, Length', 'Paragraph, Essay, Test, Answer, Line'],
            correct: 0
          },
          {
            type: 'question',
            question: 'Proofreading should check:',
            options: ['Meaning and accuracy', 'Only handwriting', 'Nothing at all'],
            correct: 0
          }
        ]
      },

      workedExample: {
        title: 'Worked Example — Improving an Answer',
        content: `
Question: How does the writer create tension?

Weak answer:
The writer creates tension by using short sentences. This makes it tense.

Improved answer:
The writer creates tension through short, fragmented sentences that make the scene feel urgent and unstable. The sentence "No sound. No movement." removes normal detail, forcing the reader to focus on the silence. This creates suspense because the lack of action suggests that something threatening may be about to happen.

Why the improved answer is better:
- It gives a clearer point.
- It uses evidence.
- It analyses sentence structure.
- It explains the reader effect.
- It links directly to tension.
`
      },

      workedExamples: [
        tier(
          'Basic',
          'The writer uses short sentences to make tension.',
          'This identifies a method but does not analyse how tension is created.'
        ),
        tier(
          'Secure',
          'The short sentence "No sound" creates tension because it makes the scene feel empty and uncertain.',
          'This is stronger because it uses evidence and explains effect.'
        ),
        tier(
          'Strong',
          'The fragments "No sound. No movement." strip the scene of action, creating a vacuum of expectation in which the reader anticipates danger.',
          'Strong because it analyses structure and gives a precise interpretation.'
        )
      ],

      practice: [
        {
          type: 'mcq',
          question: 'Which answer is strongest?',
          options: [
            'The writer uses the fragment "No sound" to create suspense because the silence suggests hidden danger.',
            'The writer uses words.',
            'It is tense because it is tense.'
          ],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'A quotation should be explained, not just included.',
          correct: true
        },
        {
          type: 'mcq',
          question: 'Writer’s purpose means:',
          options: ['Why the writer presents something in a particular way', 'Where the writer was born', 'How many pages the text has'],
          correct: 0
        },
        {
          type: 'true-false',
          question: 'Long words automatically create a Grade 9 answer.',
          correct: false
        },
        {
          type: 'mcq',
          question: 'Proofreading for accuracy includes checking:',
          options: ['Spelling, punctuation and grammar', 'Only the title', 'Only the number of paragraphs'],
          correct: 0
        }
      ],

      applicationTask: {
        title: 'Application Task',
        prompt: `Improve this weak answer:

"The writer makes the room scary by saying it was dark. This makes it scary."

Rewrite it as a stronger analytical paragraph using evidence, zoom-in analysis and reader effect.`,
        sentenceStarter: 'The writer creates fear by...',
        hints: [
          'Invent or use a short quotation such as "the darkness swallowed the corners".',
          'Zoom in on one word.',
          'Explain the effect on the reader.',
          'Avoid repeating "scary" without explanation.'
        ]
      },

      aiFeedback: {
        successCriteria: [
          'Clear analytical point',
          'Short embedded quotation',
          'Zooms in on a word or phrase',
          'Explains effect on the reader',
          'Uses precise vocabulary',
          'Avoids repetition'
        ]
      },

      mastery: {
        title: 'Mastery Challenge',
        prompt: `What is the difference between a correct answer and a high-mark answer in GCSE English?`,
        xpReward: 50
      }
    })
  ]
};

export const getEnglishContent7TopicCount = () => englishContent7.topics.length;

export default englishContent7;